import { QueryCache, QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { notifications } from '@mantine/notifications';
import {
  getHttpErrorMessage,
  isRetryableReadAxiosError,
  isRetryableReadHttpStatus,
} from '@/services/httpRetry.ts';

const MAX_QUERY_RETRIES = 2;
export const SUPPRESS_GLOBAL_ERROR_TOAST_META_KEY = 'suppressGlobalErrorToast';
export const ERROR_TOAST_TITLE_META_KEY = 'errorToastTitle';

export function shouldRetryQuery(failureCount: number, error: unknown) {
  if (failureCount >= MAX_QUERY_RETRIES) {
    return false;
  }

  if (!axios.isAxiosError(error)) {
    return true;
  }

  const status = error.response?.status;
  if (status === undefined) {
    return true;
  }

  return isRetryableReadHttpStatus(status);
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.[SUPPRESS_GLOBAL_ERROR_TOAST_META_KEY] === true) {
        return;
      }
      if (!isRetryableReadAxiosError(error)) {
        return;
      }

      notifications.show({
        title:
          typeof query.meta?.[ERROR_TOAST_TITLE_META_KEY] === 'string'
            ? query.meta[ERROR_TOAST_TITLE_META_KEY]
            : 'Unable to load data',
        message: getHttpErrorMessage(error),
        color: 'red',
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes before refetching is triggered
      refetchOnWindowFocus: true, // Prevents refetching when switching tabs
      refetchOnReconnect: true, // Auto refetch when network reconnects
      refetchOnMount: true, // Prevents unnecessary refetch on component mount
      retry: shouldRetryQuery,
    },
    // mutations: {
    //   retry: 1, // Retry failed mutations once
    // },
  },
});
export { queryClient };
